/**
 * SOURCE DE VÉRITÉ UNIQUE (côté front) du SENS d'une stat : « plus haut = mieux »
 * (défaut) ou « plus bas = mieux » (conso, coût, équipage requis, masse…).
 *
 * Pilote À LA FOIS la barre de qualité du tooltip (fillPct) ET la couleur des
 * deltas/flèches de comparaison ET la couleur bonus/malus. Avant, la logique
 * était dupliquée (mots-clés dans itemDisplay + regex dans le studio) -> oublis
 * possibles (ex. « Habitants » non détecté). Désormais : UN SEUL endroit à éditer.
 *
 * NB sur l'autorité : les AFFIXES d'instance (AttributeCatalog backend) et les
 * CARACTÉRISTIQUES d'instance (paramètre `St(..., higherIsBetter)` de MeService)
 * portent DÉJÀ leur sens depuis le backend ; le front leur fait confiance et
 * n'appelle ce helper QUE pour les stats qu'il calcule lui-même (studio à partir
 * du catalogue de types, modale vaisseau à partir des slugs, objets procéduraux).
 *
 * Accepte indifféremment une CLÉ technique (`consumption`, `fuel_consumption`,
 * `habitants`) ou un LIBELLÉ affiché (`Conso/LY`, `Habitants`, `Éner./tir`) :
 * normalisation (minuscules + accents retirés) puis match.
 */

/** Normalise : minuscules + retrait des accents (NFD décompose « é » en « e » +
 *  diacritique combinant, qu'on supprime via la plage Unicode U+0300–U+036F). */
function norm(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** Sous-chaînes « plus bas = mieux ». Tokens normalisés (sans accent). ATTENTION :
 *  `ener.` (avec le point) cible les COÛTS d'énergie (« Éner./tir », « Éner./LY »)
 *  SANS toucher la PRODUCTION d'énergie (« Énergie », « Production/LY » = +mieux). */
const LOWER_IS_BETTER_SUBSTR = [
  'conso', 'consumption', 'consommation',
  'cost', 'cout', 'price', 'upkeep', 'maintenance', 'cooldown',
  'fuel_use', 'danger', 'encounter',
  'ener.',
];

/** Clés/libellés EXACTS « plus bas = mieux » impossibles à détecter par sous-chaîne
 *  sans faux positif. Ex. `habitants` (équipage requis du cockpit : moins = mieux)
 *  qu'il ne faut PAS confondre avec `HabitantCapacity` (capacité de soute, +mieux). */
const LOWER_IS_BETTER_EXACT = new Set(['habitants', 'mass', 'masse']);

/** true = plus haut, mieux c'est (défaut) ; false = plus bas, mieux c'est. */
export function statHigherIsBetter(keyOrLabel: string): boolean {
  const k = norm(keyOrLabel);
  if (LOWER_IS_BETTER_EXACT.has(k)) return false;
  return !LOWER_IS_BETTER_SUBSTR.some((h) => k.includes(h));
}
